<script setup lang="ts">
import type { TocItemData } from '~/utils/literature/hierarchy-builder'
import { faAngleDown, faAngleRight } from '@fortawesome/pro-light-svg-icons'

const props = defineProps<{
  tocItem: TocItemData
  fallbackIndex?: number
  selectedId?: string
  disabled?: boolean
}>()

const emit = defineEmits<{ select: [tocItem: TocItemData] }>()

const fallbackLabel = computed(() =>
  typeof props.fallbackIndex === 'number' && props.fallbackIndex > 0
    ? `Del ${props.fallbackIndex}`
    : '',
)

const tocRef = useTemplateRef<HTMLElement>('toc')

const { isExpanded, setExpanded } = useTocExpandConsumer()

const children = computed(() => {
  return props.tocItem.children || []
})

const hasChildren = computed(() => {
  return children.value.length > 0
})

const showChildren = computed(() => {
  return isExpanded(props.tocItem.id)
})

function toggleChildren() {
  setExpanded(props.tocItem.id, !showChildren.value)
}

function handleNavigation() {
  if (props.disabled) {
    return
  }

  emit('select', props.tocItem)
  if (hasChildren.value) {
    setExpanded(props.tocItem.id, true)
  }
}

function hasSelectedChild(tocItem: TocItemData) {
  const itemChildren = tocItem.children
  if (!itemChildren?.length) {
    return false
  }

  const normalizedSelectedId = props.selectedId?.toLowerCase()

  for (const child of itemChildren) {
    if (normalizedSelectedId && normalizedSelectedId === child.id?.toLowerCase()) {
      return true
    }
    if (hasSelectedChild(child)) {
      return true
    }
  }

  return false
}

const isChildSelected = computed(() => hasSelectedChild(props.tocItem))
const isItemOrChildSelected = computed(() =>
  isChildSelected.value || props.selectedId?.toLowerCase() === props.tocItem.id?.toLowerCase(),
)

const { scrollToElement } = useScrollToElement('toc')

// Computed for direct selection (used in template and watcher)
const isDirectlySelected = computed(() => props.selectedId?.toLowerCase() === props.tocItem.id?.toLowerCase())

// Handle selection changes: auto-expand and scroll into view
watch([isItemOrChildSelected, isDirectlySelected, tocRef], ([isSelected, isDirect]) => {
  // Auto-expand when item or child is selected (never auto-collapse)
  if (isSelected && hasChildren.value) {
    setExpanded(props.tocItem.id, true)
  }
  // Scroll directly selected item into view
  if (isDirect && tocRef.value) {
    scrollToElement(tocRef.value)
  }
})
</script>

<template>
  <li ref="toc" class="flex flex-col gap-3xs scroll-mt-4xl dark:text-white">
    <div class="flex items-start gap-3xs text-center">
      <!-- Visitors can expand sections to preview public titles before gaining access -->
      <button
        v-if="hasChildren"
        :id="tocItem.id"
        class="flex items-center shrink-0 w-8 h-8 px-2xs mt-0.5 rounded-lg hover:bg-surface-hover dark:hover:bg-surface-hover focus:outline-none focus:ring-blurple-300"
        :class="{ 'bg-surface-primary dark:bg-surface-secondary': isExpanded(props.tocItem.id) }"
        aria-label="Vis eller skjul underpunkter"
        :aria-expanded="showChildren"
        @click="toggleChildren"
      >
        <Icon :icon="showChildren ? faAngleDown : faAngleRight" />
      </button>

      <button
        class="flex w-full items-center text-left p-2xs text-xsmall rounded-lg hover:bg-surface-hover dark:hover:bg-surface-hover focus:outline-none focus:ring-blurple-300"
        :class="{
          'bg-surface-hover dark:bg-surface-hover': isItemOrChildSelected && !isChildSelected,
          'ml-3xs': !hasChildren,
        }"
        :disabled="disabled"
        @click="handleNavigation"
      >
        {{ tocItem.title || fallbackLabel }}
      </button>
    </div>
    <ul
      v-if="showChildren && hasChildren"
      class="flex flex-col gap-3xs pl-s"
      :aria-label="tocItem.title || undefined"
    >
      <TableOfContentItem
        v-for="(child, i) in children"
        :key="child.id"
        :toc-item="child"
        :fallback-index="i + 1"
        :selected-id="selectedId"
        :disabled="disabled"
        @select="item => emit('select', item)"
      />
    </ul>
  </li>
</template>
